import React from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import CarouselComponent from "@/components/Carousel";
import Cta from "@/components/Cta";
import HomeSection from "@/components/HomeSection";
import MediaItem from "@/components/MediaItem/MediaItem";
import SideBySideContent from "@/components/SideBySideContent";
import StoreListing from "@/components/StoreListing";
import StoreListingLocator from "@/components/StoreListingLocator";
import TextHeadingImageContentSection from "@/components/TextHeadingImageContent";
import ArticleContent from "@/components/articleContent";
import EmbededScript from "@/components/embededScript";
import TextContent from "@/components/textContent";
import SlickCarousel from "@/components/CustomComponents/SlickCarousel";
import {
  FullBleedHeroImage,
  FullBleedHeroVideo,
  MultipleColumnContent,
  TwoUpContent,
} from "@kohler-dsm-library/kohler-modules";
import styles from "./index.module.scss";
import {
  formatBreadcrumbs,
  formatCTA,
  formatFullBleedHeroVideo,
  formatHeading,
  formatImage,
  formatMultiColumnContentCards,
  renderRichText,
} from "@/utils/formatters";
import PromoCollection from "@/components/CustomComponents/PromoCollection";
import FullWidthBanner from "@/components/CustomComponents/FullWidthBanner";
import ContentWithImageBanner from "@/components/CustomComponents/ContentWithImageBanner";
import ShoppingGuideCarousel from "@/components/CustomComponents/ShoppingGuideCarousel";
import ShoppingGuideImage from "@/components/CustomComponents/ShoppingGuideImage";
import TitleAndImages from "@/components/CustomComponents/TitleAndImages";
import ArticlePageCarousel from "@/components/CustomComponents/ArticlePageCarousel";
import ArticleImageIcon from "@/components/CustomComponents/ArticleImageIcon";
import ArticleCard from "@/components/CustomComponents/ArticleCard";
import PressRelease from "@/components/pressRelease";
import FeatureCardGrid from "@components/CustomComponents/FeatureCardGrid";
import SimpleContent from "@components/CustomComponents/SimpleContent";
import ActionCards from "@components/CustomComponents/ActionCards";
import EditionPattern from "@components/CustomComponents/EditionPattern";
import ContentTabs from "@components/CustomComponents/ContentTabs";
import PromotionTitle from "@components/CustomComponents/PromotionTitle";
import ProductHygieneShowcase from "@components/CustomComponents/ProductHygieneShowcase";
import ProductHighlight from "@components/CustomComponents/ProductHighlight";
import LeapSmartControl from "@components/CustomComponents/LeapSmartControl";
import PressReleaseArticle from "@/components/CustomComponents/PressReleaseArticle";
import MediaGuideSection from "@components/CustomComponents/MediaGuideSection";
import TwoUpFeaturedCard from "@components/CustomComponents/TwoUpFeaturedCard";
import LeapPersonalization from "@components/CustomComponents/LeapPersonalization";
import ProductFeatureGallery from "@components/CustomComponents/ProductFeatureGallery";
import ThreeImageCarousel from "@/components/CustomComponents/ThreeImageCarousel";
import CustomArticleTextOnlyHero from "@/components/CustomComponents/CustomArticleTextOnlyHero";
import MaterialContainer from "@/components/CustomComponents/MaterialContainer";
import FourImageCollage from "@components/CustomComponents/FourImageCollage";
import CustomCarouselBanner from "@components/CustomComponents/CustomCarouselBanner";
import FeatureVisualizationSection from "@components/CustomComponents/FeatureVisualizationSection";
import ImageTileGridSection from "@/components/CustomComponents/ImageTileGridSection";
import MediaFeatureRow from "@/components/CustomComponents/MediaFeatureRow";
import FullBleedHeroIframe from "@/components/CustomComponents/FullBleedHeroIframe";
import PhotoWallSection from "@/components/CustomComponents/PhotoWallSection";
import SiteMap from "@/components/CustomComponents/SiteMap";
import ProToolBox from "@/components/CustomComponents/ProToolBox";
import LitertureCard from "@/components/CustomComponents/LitertureCard";
import YourRoutine from "@/components/CustomComponents/YourRoutine";
import ThreePanelFeature from "@components/CustomComponents/ThreePanelFeature";
import ImageContentSplit from "@components/CustomComponents/ImageContentSplit";
import EditorialImageGrid from "@components/CustomComponents/EditorialImageGrid";
import ProjectDisplay from "@components/CustomComponents/ProjectDisplay";
import DropDownWithCheckbox from "@components/CustomComponents/DropDownWithCheckbox";
import InlineMediaBlock from "@/components/CustomComponents/InlineMediaBlock";
import ArticleDropdown from "@components/CustomComponents/ArticleDropdown";
import HorizontalRuleBlock from "@components/HorizontalRuleBlock";
import CustomIFrame from "@components/CustomComponents/IFrame";
import StoreLocator from "@components/CustomComponents/LocatorStore";
import PromotionStoreList from "@components/PromotionStoreList";
import ArticleContactMethods from "@components/CustomComponents/ArticleContactMethods";
import WalkinCarousel from "@components/WalkinCarousel";
import ContactUs from "@components/CustomComponents/ContactUs";
import ProductInfoCard from "@components/CustomComponents/ProductInfoCard";
import ProductDetails from "@components/CustomComponents/ProductDetails";
import KecContent from "@components/CustomComponents/KecContent";

const Section = ({ fields, sys, pageHeading }) => {
  const { locale } = useRouter();
  const getResolvedHeading = () =>
    pageHeading || fields?.heading?.content?.[0]?.content?.[0]?.value;

  const sectionType = sys?.contentType?.sys?.id;

  const renderSection = () => {
    const resolvedHeading = getResolvedHeading();

    switch (sectionType) {
      case "slickCarousel":
        return (
          <SlickCarousel images={fields?.images} heading={fields?.heading} />
        );

      case "walkinCarousel":
        return <WalkinCarousel walkinCarouselData={fields} />;

      case "promotionStoreList":
        return <PromotionStoreList promotionData={fields} />;

      case "articleContent":
        return <ArticleContent heading={resolvedHeading} fields={fields} />;

      case "locator":
        return <StoreListing pageHeading={resolvedHeading} fields={fields} />;

      case "form":
        return (
          <StoreListingLocator pageHeading={resolvedHeading} fields={fields} />
        );

      case "carousel":
        return <CarouselComponent carouselData={fields} />;

      case "textContentSection":
      case "richtext":
        return <TextContent fields={fields} pageHeading={pageHeading} />;

      case "embed":
        return <EmbededScript fields={fields} pageHeading={pageHeading} />;

      case "cta":
        return <Cta fields={fields} />;

      case "storeLocator":
        return <StoreLocator locatorData={fields} />;

      case "imageContentSection":
      case "mediaItem":
        return (
          <MediaItem
            assets={fields?.mediaItem || [{ fields }]}
            height="550px"
          />
        );

      case "textHeadingImageContentSection":
        return <TextHeadingImageContentSection fields={fields} />;

      case "sideBySideContentBlock":
        return <SideBySideContent fields={fields} />;

      case "horizontalRuleBlock":
        return <HorizontalRuleBlock />;

      case "multipleColumnContent":
        return (
          <MultipleColumnContent
            className={styles.multipleColumnContent}
            mod={fields?.mod}
            numberOfColumns={fields?.numberOfColumns}
            sliderOnSmallScreens={false}
            eyebrow={
              fields?.eyebrow && {
                text: renderRichText(fields?.fields?.eyebrow, locale),
              }
            }
            heading={{ text: fields?.heading?.fields?.text, component: "h2" }}
            copy={{ text: renderRichText(fields?.copy, locale) }}
            cards={formatMultiColumnContentCards(fields?.cards, locale)}
          />
        );

      case "twoUpContent":
        return (
          <TwoUpContent
            className={styles.twoUpContent}
            mod={fields?.mod}
            type="50-50"
            imageOrientation="landscape"
            heading={
              fields?.heading?.fields?.text && {
                text: fields?.heading?.fields?.text,
                component: "h2",
              }
            }
            CTALink={formatCTA(fields?.cta, locale)}
            description={fields?.description && { text: fields?.description }}
            cards={fields?.cards.map(card => ({
              title: {
                text: card?.fields?.title?.fields?.text,
                component: "h3",
                className: `${fields?.mod} ${
                  fields?.mod === "white" ? "!text-black" : "!text-[#FFFFFF]"
                }`,
              },
              description: {
                text: card?.fields?.description,
                className: `${fields?.mod} ${
                  fields?.mod === "white" ? "!text-black" : "!text-[#FFFFFF]"
                }`,
              },

              CTA: formatCTA(card?.fields?.cta, locale),
              image: {
                src: card?.fields?.image?.fields?.imageUrl,
                alt: card?.fields?.image?.fields?.altText || "",
                loading: "lazy",
                role: "presentation",
              },
            }))}
          />
        );

      case "cardSection":
        return <HomeSection homePageData={fields} />;

      case "articlePageCarousel":
        return <ArticlePageCarousel fields={fields} />;

      case "shoppingGuideCarousel":
        return (
          <ShoppingGuideCarousel
            shoppingGuideCarouselData={fields}
            locale={locale}
          />
        );

      case "shoppingGuideImage":
        return <ShoppingGuideImage shoppingGuideImageData={fields} />;

      case "titleAndImages":
        return <TitleAndImages titleAndImagesData={fields} />;

      case "promoCollection":
        return <PromoCollection collectionItems={fields} />;

      case "fullWidthBanner":
        return <FullWidthBanner bannerProps={fields} />;

      case "contentWithImageBanner":
        return <ContentWithImageBanner contentWithImageBannerProps={fields} />;

      case "articleImageIcon":
        return <ArticleImageIcon articleImageIconProps={fields} />;

      case "articleCard":
        return <ArticleCard articleData={fields} />;

      case "pressRelease":
        return <PressRelease pageSections={fields} pageHeading={pageHeading} />;

      case "pressReleaseArticle":
        return <PressReleaseArticle fields={fields} />;

      case "featureCardGrid":
        return <FeatureCardGrid featureCardGridData={fields} />;

      case "threeImageCarousel":
        return <ThreeImageCarousel threeImageCarouselData={fields} />;

      case "customArticleTextOnlyHero":
        return (
          <CustomArticleTextOnlyHero customArticleTextOnlyHeroData={fields} />
        );

      case "threePanelFeature":
        return <ThreePanelFeature threePanelFeatureData={fields} />;

      case "imageContentSplit":
        return <ImageContentSplit imageContentSplitData={fields} />;

      case "editorialImageGrid":
        return <EditorialImageGrid editorialImageGridData={fields} />;

      case "fullBleedHeroImage":
        return (
          <FullBleedHeroImage
            className={`max-h-215 ${styles.fullBleedHeroImage}`}
            mod={fields?.mod}
            heading={{ text: fields?.heading?.fields?.text, component: "span" }}
            subheading={{ text: fields?.subheading?.fields?.text }}
            image={{
              src: fields?.image?.fields?.imageUrl,
              alt: fields?.image?.fields?.altText || "",
              loading: "lazy",
              role: "presentation",
              objectFit: "unset",
              height: "unset",
            }}
            opacity={fields?.opacity || 0}
            textPlacement={fields?.textPlacement}
            fullScreenTint={fields?.fullScreenTint || false}
          />
        );

      case "fullBleedHeroVideo":
        return (
          <FullBleedHeroVideo
            mod={fields?.mod}
            heading={formatHeading(fields?.heading)}
            subheading={{ text: renderRichText(fields?.subheading, locale) }}
            breadcrumbs={formatBreadcrumbs(fields?.breadcrumbs)}
            CTAs={fields?.cta?.map(cta => formatCTA(cta, locale))}
            opacity={fields?.opacity}
            showNextCTA={fields?.showNextCTA}
            video={formatFullBleedHeroVideo(
              fields?.video,
              fields?.video?.fields?.poster,
              fields?.video?.fields?.posterImages
            )}
            gradientDirection={fields?.fields?.gradientDirection}
            textPlacement={fields?.fields?.textPlacement}
          />
        );

      case "iframe":
        return <CustomIFrame iframeData={fields} />;

      case "simpleContent":
        return <SimpleContent simpleContentData={fields} />;

      case "actionCards":
        return <ActionCards actionCardsData={fields} />;

      case "editionPattern":
        return <EditionPattern patternData={fields} />;

      case "contentTabs":
        return <ContentTabs dynamicTabData={fields} />;

      case "promotionTitle":
        return <PromotionTitle promotionTitleData={fields} />;

      case "productHygieneShowcase":
        return <ProductHygieneShowcase productHygieneShowcaseData={fields} />;

      case "productHighlight":
        return <ProductHighlight productHighlightsData={fields} />;

      case "leapSmartControl":
        return <LeapSmartControl leapSmartControlData={fields} />;

      case "mediaGuideSection":
        return <MediaGuideSection mediaGuideSectionData={fields} />;

      case "twoUpFeaturedCard":
        return <TwoUpFeaturedCard twoUpFeaturedCardData={fields} />;

      case "leapPersonalization":
        return <LeapPersonalization leapPersonalizationData={fields} />;

      case "productFeatureGallery":
        return <ProductFeatureGallery productFeatureGalleryData={fields} />;

      case "materialContainer":
        return <MaterialContainer materialData={fields} />;

      case "fourImageCollage":
        return <FourImageCollage fourImageCollageData={fields} />;

      case "customCarouselBanner":
        return <CustomCarouselBanner customCarouselBannerData={fields} />;

      case "featureVisualizationSection":
        return (
          <FeatureVisualizationSection featureVisualizationData={fields} />
        );
      case "imageTileGridSection":
        return <ImageTileGridSection fields={fields} />;

      case "mediaFeatureRow":
        return <MediaFeatureRow fields={fields} />;

      case "fullBleedHeroIframe":
        return <FullBleedHeroIframe fields={fields} />;

      case "photoWallSection":
        return <PhotoWallSection photoWallSectionData={fields} />;

      case "siteMap":
        return <SiteMap siteMapData={fields} />;

      case "literatureCard":
        return <LitertureCard CardData={fields} />;

      case "proToolBox":
        return <ProToolBox toolBoxData={fields} />;

      case "yourRoutine":
        return <YourRoutine routineData={fields} />;

      case "projectDisplay":
        return <ProjectDisplay projectDisplayData={fields} />;

      case "dropDownProps":
        return <DropDownWithCheckbox dropDownData={fields} />;

      case "inlineMediaBlock":
        return <InlineMediaBlock inlineMediaBlockData={fields} />;

      case "articleDropdown":
        return <ArticleDropdown articleDropdownData={fields} />;

      case "articleContactMethods":
        return <ArticleContactMethods articleContactData={fields} />;

      case "productListCategory":
        return null;

      case "contactUsLandingIndonesia":
        return <ContactUs data={fields} />;

      case "productInfoCard":
        return <ProductInfoCard productInfoCardData={fields} />;

      case "productDetails":
        return <ProductDetails productDetailsData={fields} />;

      case "kecContent":
        return <KecContent kecContentData={fields} />;

      default:
        return <h1 className="text-[black]">{sectionType}</h1>;
    }
  };

  return renderSection();
};

Section.propTypes = {
  fields: PropTypes.object.isRequired,
  sys: PropTypes.shape({
    contentType: PropTypes.shape({
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  pageHeading: PropTypes.string,
};

export default Section;
